import { format } from "date-fns";

const isNullSentinelDate = (d: Date): boolean => d.getFullYear() <= 1900;

const HAS_TIMEZONE = /([zZ]|[+-]\d{2}:?\d{2})$/;

/**
 * Parses a server timestamp into a Date. The API returns UTC timestamps without
 * a timezone designator (e.g. "2026-06-18T17:20:22"), which `new Date()` would
 * otherwise interpret as local time. When no timezone is present and the value
 * includes a time component, it is treated as UTC so it converts correctly to
 * the user's local timezone.
 */
export const parseServerDateTime = (value: string): Date => {
	const trimmed = value.trim();
	const hasTime = trimmed.includes("T") || /\d{1,2}:\d{2}/.test(trimmed);
	const normalized =
		hasTime && !HAS_TIMEZONE.test(trimmed) ? `${trimmed}Z` : trimmed;
	return new Date(normalized);
};

export const dateFormatter = (date: string) => {
	const validDate = date.replaceAll("-", "/");
	if (validDate === "") return;
	const d = new Date(validDate);
	if (isNullSentinelDate(d)) return;
	return d.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "2-digit",
	});
};
export const formatDateToNumeric = (
	date: string | null | undefined,
): string => {
	if (!date) return "";
	const d = new Date(date);
	if (Number.isNaN(d.getTime())) return "Invalid Date";
	if (isNullSentinelDate(d)) return "";
	return d.toLocaleDateString("en-US", {
		month: "2-digit",
		day: "2-digit",
		year: "numeric",
	});
};
export const formatDateTime = (date: string | null | undefined): string => {
	if (!date) return "";
	const d = new Date(date);
	if (Number.isNaN(d.getTime())) return "Invalid Date";
	if (isNullSentinelDate(d)) return "";
	return d.toLocaleString("en-US", {
		month: "2-digit",
		day: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: true,
	});
};

/**
 * Formats a server timestamp as `MM/dd/yyyy hh:mm a` (e.g. "06/29/2026 02:30 PM").
 * Returns an empty string when the date is missing.
 */
export const formatSourceDate = (date: string | null | undefined): string =>
	date ? format(new Date(date), "MM/dd/yyyy hh:mm a") : "";
